<template>
  <MarketplaceCard
    v-if="staxPayments.config"
    v-model="switchOn"
    :disabled="switchDisabled"
    :logo-path="imageUrl"
    logo-alt="Stax Payments"
    description="Accept every payment type and connect all the tools you need under a single dashboard."
    learn-more-link="https://get.sera.tech/sera-payments/"
    cy-selector="marketplaceStax"
  >
    <template #configure>
      <div>
        <div class="link" @click="openModal">Set Up Information</div>
      </div>
    </template>
    <template #modal>
      <div>
        <Modal
          v-if="modalOpen"
          title="Set Up Information - Stax Payments"
          class-name="jb-warranties-config-modal"
          submit-btn-text="Save"
          :saving="saving"
          @submit="saveIntegration"
          @delete="confirmDelete"
          @hide-modal="closeModal"
        >
          <div class="d-flex flex-column">
            <InputField
              v-model="staxPayments.config.api_key"
              class="!mb-0"
              title="API Auth Key"
              type="password"
              toggleable
              :validation="
                v$.staxPayments.config.api_key.$error ? 'error' : 'none'
              "
              :helper-text="
                v$.staxPayments.config.api_key.$errors[0]?.$message || null
              "
            />
            <hr class="border-gray-200 mt-3 mb-1" />
            <Checkbox
              v-model="staxPayments.config.ach_enabled"
              label="Allow customers ability to pay with ACH"
              class="mt-2"
            />
            <Checkbox
              v-model="staxPayments.config.card_savable"
              label="Allow customers to save their payment method"
              class="mt-2"
            />
            <Checkbox
              v-model="staxPayments.config.card_readers"
              label="Activate technician app card reader support"
              class="mt-2"
            />
          </div>
        </Modal>
        <Modal
          v-if="deleteConfirm"
          title="Delete Comment"
          @hide-modal="toggleDeleteConfirm"
        >
          <div>
            This configuration will be permanently deleted and you will not be
            able to undo this action. Are you sure?
          </div>
          <template #submit>
            <Button
              id="submit_modal_btn"
              type="danger"
              @click="dropIntegration"
            >
              Delete Configuration
            </Button>
          </template>
        </Modal>
      </div>
    </template>
  </MarketplaceCard>
</template>

<script>
import { mapActions, mapState } from "vuex";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import StaxPaymentsLogo from "@/images/staxpay_logo.png";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import MarketplaceCard from "./MarketplaceCard.vue";

export default {
  components: {
    MarketplaceCard,
    Modal,
    InputField,
    Button,
    Checkbox
  },
  data() {
    return {
      deleteConfirm: false,
      switchDisabled: null,
      switchOn: null,
      modalDropVisible: null,
      modalOpen: false,
      saving: false
    };
  },
  computed: {
    ...mapState(["staxPayments"]),
    imageUrl: {
      get() {
        return StaxPaymentsLogo;
      }
    }
  },
  watch: {
    switchOn: function (newValue, oldValue) {
      if (
        oldValue === null ||
        newValue === oldValue ||
        newValue === this.staxPayments.config.enabled
      ) {
        return;
      }
      newValue ? this.enableIntegration() : this.disableIntegration();
    },
    "staxPayments.config.enabled": function (newValue, oldValue) {
      if (oldValue !== newValue) {
        this.refreshTools();
      }
    },
    "staxPayments.config.api_key": function (newValue, oldValue) {
      if (oldValue !== null && newValue === null) {
        this.refreshModal();
      }
    }
  },
  mounted() {
    this.fetchStaxPayments().then(this.refreshTools);
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      staxPayments: {
        config: {
          api_key: { required, $autoDirty: true }
        }
      }
    };
  },
  methods: {
    ...mapActions([
      "fetchStaxPayments",
      "changeStaxPayments",
      "dropStaxPayments"
    ]),
    openModal() {
      this.fetchStaxPayments().then(() => {
        this.modalOpen = true;
        this.refreshModal();
      });
    },
    closeModal() {
      return this.fetchStaxPayments().then(() => {
        this.modalOpen = false;
        this.refreshTools();
      });
    },
    enableIntegration() {
      this.changeStaxPayments({ enabled: true })
        .then(() => {
          window.notify.success("Stax Payments integration enabled!");
        })
        .catch(() => (this.switchOn = false));
    },
    disableIntegration() {
      this.changeStaxPayments({ enabled: false }).then(() => {
        window.notify.success("Stax Payments integration disabled!");
      });
    },
    async saveIntegration() {
      try {
        this.saving = true;
        await this.changeStaxPayments(this.staxPayments.config);
        window.notify.success("Stax Payments settings saved!");
        this.closeModal();
      } catch (e) {
        // form shows validation errors
      }
      this.saving = false;
    },
    dropIntegration() {
      this.deleteConfirm = false;
      this.closeModal().then(() => {
        this.switchOn = false;
        if (this.staxPayments.config.persisted) {
          this.dropStaxPayments().then(() => {
            window.notify.success("Stax Payments settings deleted!");
          });
        } else {
          window.notify.error(
            "The integration has not been configured yet. There's nothing to delete."
          );
        }
      });
    },
    // Make the state consistent
    refreshTools() {
      this.switchOn = this.staxPayments.config.enabled;
      if (this.switchDisabled) {
        this.switchOn = false;
      }
    },
    refreshModal() {
      if (!this.modalOpen) {
        return;
      }

      // remove blank values
      if (this.staxPayments.config.api_key === "") {
        this.staxPayments.config.api_key = null;
      }
    },
    confirmDelete() {
      this.deleteConfirm = true;
    },
    toggleDeleteConfirm() {
      this.deleteConfirm = !this.deleteConfirm;
    }
  }
};
</script>
